import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import {
  closeLifecycleFocusWindow,
  focusForLifecycle,
  openLifecycleFocusWindow,
} from "../foundations/lifecycleFocus";
import { overlayZIndex, escapeLayerSelector } from "../foundations/popover";
import { PopupOwnerContext } from "../foundations/popupOwner";
import { xyScrollbarStyles } from "../foundations/scrollbar";
import {
  baseRestoreTarget,
  isTopmostModal,
  MODAL_EXITING_ATTR,
  registerModal,
  setModalLevel,
  setModalMountNode,
  topmostModalEl,
} from "./modalStack";
import { isNonEmptyString } from "../../utils/reactNode";
import { devWarn } from "../../utils/devWarning";
import { prefersReducedMotion } from "../../utils/prefersReducedMotion";
import { ModalCloseContext, ModalLabelContext, type ModalLabelRegistry } from "./modalContext";
import { ModalBodyRoot, ModalFooterRoot, ModalHeader } from "./ModalParts";

export type ModalSize = "sm" | "md" | "lg";

const MAX_WIDTH: Record<ModalSize, string> = {
  sm: "24rem",
  md: "32rem",
  lg: "48rem",
};

export type ModalLevel = "media" | "low" | "default" | "high" | "top";

// level → backdrop z-index. media는 MediaOverlay 전용 하단층이고, 일반 4층(low < default < high < top)은
// 앱이 화면 겹침을 매핑. low 이후 100 간격은 비-Modal 오버레이가 끼어도 순서 보존(popover.ts §overlayZIndex).
// backdrop만 z를 갖고 className/style은 Dialog로 가므로 외부 스택 조정 경로는 이 prop뿐.
const LEVEL_Z: Record<ModalLevel, number> = {
  media: overlayZIndex.media,
  low: overlayZIndex.low,
  default: overlayZIndex.default,
  high: overlayZIndex.high,
  top: overlayZIndex.top,
};

// Tab cycle wrap 양 끝(첫/마지막 tabbable) 탐지용 selector.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// selector match 중 실제 tabbable만 — inert/aria-hidden 영역, 숨겨진 요소는 focus 후보 제외.
type CheckVisibilityOptions = {
  visibilityProperty?: boolean;
  contentVisibilityAuto?: boolean;
  opacityProperty?: boolean;
};
const isTabbable = (el: HTMLElement): boolean => {
  // inert / aria-hidden 영역은 focus 트리에서 분리.
  if (el.closest("[inert]")) return false;
  if (el.closest('[aria-hidden="true"]')) return false;
  // 음수 tabindex 전부 제외 (selector는 "-1"만 거름).
  if (el.tabIndex < 0) return false;
  // display/visibility/content-visibility로 숨겨진 요소 제외 (opacity:0은 tabbable 유지). checkVisibility는
  // 기본적으로 visibility/content-visibility를 안 보므로 옵션 명시. 미지원 환경은 위 체크로 충분.
  const check = (
    el as HTMLElement & {
      checkVisibility?: (options?: CheckVisibilityOptions) => boolean;
    }
  ).checkVisibility;
  if (typeof check === "function") {
    return check.call(el, { visibilityProperty: true, contentVisibilityAuto: true });
  }
  return true;
};

const getTabbables = (dialog: HTMLElement): HTMLElement[] =>
  Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isTabbable);

// enter/exit 모션 길이(ms) — CSS transition + close 시 unmount 지연이 공유. 300ms = 기존 sigmakit
// Overlay와 동일. 모션 토큰 미정이라 인라인 리터럴.
const MOTION_MS = 300;

// 모달 라이프사이클 — open/close 한 어휘로 통일. rendered(portal 존재)·active(시각 열림)는 phase에서 파생.
//   closed  : portal 없음
//   open    : 보임(opacity 1). enter 페이드 시작점(opacity 0)은 CSS `@starting-style`이 portal 삽입 시 제공 — JS flip 불필요.
//   closing : open=false 받고 페이드아웃 중, MOTION_MS 뒤 "closed"로 unmount
// 흐름: onClose(요청) → [closing] → onClosed(완료). reduced-motion은 closing 스킵(즉시 closed).
type ModalPhase = "closed" | "open" | "closing";

// 불변식: Backdrop은 `inset:0` full-viewport + transform 없음 유지 — 모달 안 AnchoredPopup은 Dialog에
// mount되지만 그 Panel이 `position:fixed`로 viewport 좌표를 쓴다. Backdrop에 transform/filter/perspective/
// will-change 추가나 full-bleed 깨짐은 Backdrop을 fixed-descendant의 containing block으로 만들어 좌표가 어긋난다.
// (`backdrop-filter`도 containing block을 만들지만 full-viewport라 viewport rect와 같아 무영향.)
const Backdrop = styled.div<{ $active: boolean; $blur: boolean; $z: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => $z};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  /* backdropBlur: backdrop 뒤(페이지)만 blur — 자식 dialog는 위라 무영향. scrim과 함께 fade. */
  backdrop-filter: ${({ $blur, $active }) => ($blur && $active ? "blur(4px)" : "none")};
  -webkit-backdrop-filter: ${({ $blur, $active }) => ($blur && $active ? "blur(4px)" : "none")};
  transition: backdrop-filter ${MOTION_MS}ms ease;

  /* scrim: solid black 토큰(RFC-semantic-token-map) + dim 알파, enter/exit 시 0↔0.4 fade. ::before라 dialog엔 영향 없음. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: ${({ theme }) => theme.color.scrim.default};
    opacity: ${({ $active }) => ($active ? 0.4 : 0)};
    transition: opacity ${MOTION_MS}ms ease;
    pointer-events: none;
  }

  /* enter 시작점 — portal 삽입 시 scrim(0)·blur(none)에서 fade in. 미지원 엔진은 즉시 표시(graceful, reduced-motion과 동일). */
  @starting-style {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    &::before {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::before {
      transition: none;
    }
  }
`;

const Dialog = styled.div<{ $maxWidth: string; $maxHeight?: string; $active: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth};
  /* 뷰포트보다 길면 Modal.Body가 header/footer 사이에서 스크롤(backdrop padding 1rem×2 = 2rem 제외). */
  max-height: ${({ $maxHeight }) =>
    $maxHeight ? `min(${$maxHeight}, calc(100vh - 2rem))` : "calc(100vh - 2rem)"};
  /* Modal.Body가 없는 legacy/unwrapped content는 Dialog가 fallback scroll ancestor가 된다. */
  ${xyScrollbarStyles}
  background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
  color: ${({ theme }) => theme.color.text.default};
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  box-shadow: ${({ theme }) => theme.elevation.shadow.modal};
  /* 프로그래매틱 focus 컨테이너 — ring 안 그림(내부 컨트롤이 각자 :focus-visible). */
  outline: none;

  /* enter/exit: opacity fade만(스케일 없음). RFC-modal-primitive §동작 7. */
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity ${MOTION_MS}ms ease;
  /* enter 시작점 — portal 삽입 시 opacity 0에서 fade in(JS flip 불필요). 미지원 엔진은 즉시 표시(graceful). */
  @starting-style {
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export interface ModalProps {
  /** 표시 여부 — 항상 controlled. */
  open: boolean;
  /** 사용자 dismiss 시 호출 — Escape / backdrop 클릭 / `children`에 둔 닫기 버튼 모두 여기로. */
  onClose: () => void;
  /**
   * 닫힘 모션 종료 + portal 제거 직후 1회 호출 — mount-on-open consumer가 `MOTION_MS` 타이머 없이
   * 페이드 후 모달을 언마운트하게. reduced-motion이면 close 즉시. 중단된 닫힘(페이드 중 재-open)·
   * 하드 언마운트(`open=true`인 채 제거)엔 호출 안 함.
   */
  onClosed?: () => void;
  /**
   * dialog 내용. 헤더/타이틀/푸터/닫기 버튼 구조는 호출자가 여기에 넣는다 (composition — prop 아님).
   * `<Modal.Header.Title>`을 넣으면 그 타이틀이 자동으로 dialog의 accessible name이 된다.
   */
  children: ReactNode;
  /** width 프리셋(max-width). 기본 `"md"`. 더 큰/특수 폭은 `style`/`className`으로. */
  size?: ModalSize;
  /**
   * dialog 최대 높이 cap(CSS length, 예: `"32rem"`, `"70vh"`). 미지정 시 viewport cap만 적용.
   * 지정해도 viewport 안전선(`calc(100vh - 2rem)`)을 넘지 않는다. content는 auto-height를 유지하고,
   * 이 cap을 넘으면 `<Modal.Body>`가 header/footer 사이에서 스크롤된다. `size`(width cap)와 대칭.
   */
  maxHeight?: string;
  /** Escape로 닫기. 기본 `true` — unsaved-changes 류는 `false`. */
  closeOnEscape?: boolean;
  /** backdrop 클릭으로 닫기. 기본 `true`. */
  closeOnBackdropClick?: boolean;
  /** backdrop 뒤(페이지)를 `backdrop-filter: blur`로 흐리게. 기본 `false`. scrim dim과 함께 enter/exit fade. */
  backdropBlur?: boolean;
  /**
   * 스태킹 층 — backdrop z-index 결정. `"media"` < `"low"` < `"default"`(기본) < `"high"` < `"top"`.
   * `"media"`는 c6 MediaOverlay 전용 하단층이고, 나머지 일반층은 앱이 화면 겹침을 매핑한다.
   * 다른 오버레이가 있는 통합 환경에서만 의미하며 단독 시 외관은 동일하다.
   */
  level?: ModalLevel;
  /**
   * dialog의 accessible name — `aria-label` 또는 `aria-labelledby`.
   * 둘 다 안 주면 `children`의 `<Modal.Header.Title>`이 자동으로 이름을 제공한다(타이틀 `<h2>` id를
   * 등록 → Modal이 `aria-labelledby`로 연결). 셋 다 없을 때만 dev warn.
   * 명시한 `aria-label`/`aria-labelledby`는 자동 배선보다 우선한다.
   */
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  /** dialog 박스에 적용 (backdrop엔 안 감). */
  className?: string;
  style?: CSSProperties;
}

/**
 * Modal dialog — backdrop + 중앙 dialog + portal/focus/scroll lock/Escape·backdrop dismiss.
 *
 * - **controlled**: `open` + `onClose`. open 시 `document.body`에 portal container(`<div data-c6-modal-root>`)를
 *   만들어 거기에 렌더, close/unmount 시 제거.
 * - **focus**: open 시 dialog 컨테이너(`tabIndex={-1}`, ring 없음)로 이동 — 첫 control로 주면 자동 오픈
 *   모달에서 브라우저 `:focus-visible` 휴리스틱이 링을 그린다. Tab이 첫 control로 진입하고, children이
 *   이미 focus를 잡았으면(consumer `autoFocus`) 존중한다. close 시 직전 요소로 복원.
 *   바깥(body 직속 자식)은 `inert` — close 시 우리가 추가한 것만 제거.
 * - **scroll lock**: open 동안 `<body> { overflow: hidden }`, close 시 이전 값 복원.
 * - **dismiss**: Escape(`closeOnEscape`) / backdrop 클릭(`closeOnBackdropClick` — pointer down·up이 둘 다
 *   backdrop일 때만). 닫기 버튼·헤더·푸터·타이틀은 모두 composition(`children`).
 * - **a11y**: `role="dialog"` + `aria-modal="true"` + accessible name. 이름은 `aria-label`/`aria-labelledby`로
 *   직접 주거나, `children`의 `<Modal.Header.Title>`이 자동 연결(context로 타이틀 id 등록). 셋 다 없으면 dev warn.
 *   나머지 페이지는 `inert`.
 * - **backdropBlur**: `backdropBlur`면 backdrop 뒤 페이지를 `blur(4px)`로 흐리게(scrim dim과 함께 fade). 기본 off.
 * - **level**: backdrop z-index 스택 층 — `"media" < "low" < "default"(기본) < "high" < "top"`. 다른 오버레이 대비 위/아래 배치(앱이 의미 매핑).
 * - **stacking(modal 위 modal)**: depth-2(잠금 위 dialog)까지 안전 — scroll lock(ref-count) + 바깥 inert(topmost만 인터랙티브) + focus를 `modalStack`이 조율. 더 깊은 중첩도 z 순서로 동작하나 1차 보증은 2겹.
 * - light/dark는 `data-c6-theme` CSS 변수로 자동 resolve.
 */
function ModalRoot({
  open,
  onClose,
  onClosed,
  children,
  size = "md",
  maxHeight,
  closeOnEscape = true,
  closeOnBackdropClick = true,
  backdropBlur = false,
  level = "default",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  className,
  style,
}: ModalProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  // 라이프사이클 phase 하나로 관리 — rendered(portal 존재)·active(시각 열림)는 여기서 파생.
  // 초기: open이면 "open"(enter 페이드는 CSS @starting-style이 portal 삽입 시 처리), 닫혀 있으면 "closed".
  const [phase, setPhase] = useState<ModalPhase>(() => (open ? "open" : "closed"));
  const rendered = phase !== "closed";
  const active = phase === "open";
  // open-sync effect의 exit 가드가 현재 phase를 읽기 위함 — phase를 deps에 넣어 effect를 재실행시키지 않으려고 ref로.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // backdrop 클릭으로만 닫음 — pointer down/up 둘 다 backdrop일 때만.
  const pointerDownOnBackdrop = useRef(false);
  // 제스처 시작(pointerdown)에 popup(escape-layer)이 떠 있었는지 — 떠 있었으면 backdrop 클릭은 popup만 닫고
  // (usePopoverDismiss) 모달은 보존(Escape 실드와 대응). popup은 pointerdown에 닫혀 click 시점엔 늦으므로 기록.
  const escapeLayerAtPointerDown = useRef(false);
  // 스택 등록은 `rendered` effect에서 한 번만(level 변경 시 portal remount 회피), 초기 z는 ref로 읽음.
  // open 중 level 변경은 아래 setModalLevel sync effect가 반영.
  const zRef = useRef(LEVEL_Z[level]);
  zRef.current = LEVEL_Z[level];

  // 자동 accessible-name 배선: children의 <Modal.Header.Title>이 자기 <h2> id를 등록하면 여기에 담겨
  // aria-labelledby로 연결된다. ref(동기) + state(속성 반영) 두 갈래 — ref는 경고 effect가 자식 등록을
  // 즉시 읽기 위함(자식 effect는 부모 effect보다 먼저 실행되므로 ref가 채워진 뒤 부모 경고가 돈다).
  const registeredTitleIdRef = useRef<string | undefined>(undefined);
  const [autoLabelledBy, setAutoLabelledBy] = useState<string | undefined>(undefined);
  const labelRegistry = useMemo<ModalLabelRegistry>(
    () => ({
      register: (titleId) => {
        registeredTitleIdRef.current = titleId;
        setAutoLabelledBy(titleId);
      },
      unregister: (titleId) => {
        if (registeredTitleIdRef.current !== titleId) return;
        registeredTitleIdRef.current = undefined;
        setAutoLabelledBy(undefined);
      },
    }),
    [],
  );

  const hasAriaLabel = isNonEmptyString(ariaLabel);
  const hasAriaLabelledBy = isNonEmptyString(ariaLabelledBy);
  // 호출자가 이름을 직접 주지 않은 경우에만 Modal.Header.Title로 자동 연결(명시값이 우선).
  const resolvedLabelledBy = hasAriaLabelledBy
    ? ariaLabelledBy
    : hasAriaLabel
      ? undefined
      : autoLabelledBy;

  // accessible name 누락 경고 — container가 생긴 뒤(=children/Modal.Header.Title이 portal에
  // 마운트되어 등록을 마친 뒤)에만 판정해야 false-positive가 없다. 타이틀 없는 진짜 무명 케이스만 경고.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (!container || warnedRef.current) return;
    if (hasAriaLabel || hasAriaLabelledBy || registeredTitleIdRef.current) return;
    warnedRef.current = true;
    devWarn(
      "[c6] <Modal> requires an accessible name. Provide `aria-label`, `aria-labelledby`, or a <Modal.Header.Title> child.",
    );
  }, [container, hasAriaLabel, hasAriaLabelledBy]);

  // open ↔ phase 동기화.
  // - enter: 바로 "open" — enter 페이드(opacity 0→1 시작점)는 Dialog/Backdrop의 CSS @starting-style이 portal
  //   삽입 시 제공하므로 JS flip(opening phase/RAF)이 필요 없다.
  // - exit: "closing"으로 페이드 후 MOTION_MS 뒤 "closed"로 unmount. 그동안 scroll lock/inert/focus trap 유지.
  //   reduced-motion이면 페이드 없이 즉시 "closed".
  useEffect(() => {
    if (open) {
      setPhase("open");
      return;
    }
    // 이미 닫힌 채 마운트/리렌더면 closing 진입 스킵 — open=false 초기 마운트가 invisible dialog를 MOTION_MS
    // 동안 렌더(modalStack focus/inert/scroll-lock 오염)하던 phantom 제거.
    if (phaseRef.current === "closed") return;
    if (prefersReducedMotion()) {
      setPhase("closed");
      return;
    }
    setPhase("closing");
    const timer = window.setTimeout(() => setPhase("closed"), MOTION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  // 닫힘 완료(→"closed" 전이) 직후 onClosed 1회 호출. ref로 들어 deps에서 빼 — 새 onClosed 클로저가
  // 와도 open-sync effect를 재실행시키지 않게.
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const prevPhaseRef = useRef(phase);
  useEffect(() => {
    if (prevPhaseRef.current !== "closed" && phase === "closed") onClosedRef.current?.();
    prevPhaseRef.current = phase;
  }, [phase]);

  // rendered면: 직전 focus 저장 → portal container 생성 → 스택 등록(modalStack이 scroll lock·바깥 inert·focus
  // 조율 — 모달이 겹쳐도 안 엉키게). cleanup에서 해제 후, 남은 모달 있으면 그 topmost로, 없으면 직전 요소로 복원.
  useEffect(() => {
    if (!rendered) return;
    const restoreFocusTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const el = document.createElement("div");
    el.setAttribute("data-c6-modal-root", "");
    document.body.appendChild(el);

    // children의 consumer autoFocus는 다음 commit에서 marker 없이 focus를 쏜다 — portal root 단위
    // mount window로 그 commit을 lifecycle focus로 취급(Tooltip 미노출). 닫기는 아래 layout effect.
    openLifecycleFocusWindow(el);
    const unregister = registerModal(el, zRef.current, restoreFocusTo);
    setContainer(el);

    return () => {
      closeLifecycleFocusWindow(el);
      // teardown 전 캡처(el.remove()/inert가 포커스를 body로 날리기 전): topmost였나 / 포커스 주인이었나.
      // 배경(하위) 모달이 닫힐 땐 주인이 아니라 위 모달 포커스를 안 건드린다 — 안 그러면 위 모달에서 깊이
      // 둔 포커스를 dialog 컨테이너로 되돌려 뺏는다.
      const wasTopmost = isTopmostModal(el);
      const ownedFocus = el.contains(document.activeElement);
      setContainer(null);
      el.remove();
      unregister();
      if (!wasTopmost && !ownedFocus) return;
      // focus 주인이었으면: 아래 남은 topmost로 넘기고, 마지막이면 직전 요소로 복원.
      const nextTop = topmostModalEl();
      if (nextTop) {
        focusForLifecycle(nextTop.querySelector<HTMLElement>('[role="dialog"]'));
      } else {
        // 스택이 다 비면 첫 모달 opener로 복원 — close-all에서 마지막 모달의 자체 타깃은 이미 제거된 하위
        // 모달 내부를 가리킬 수 있다. 끊긴 타깃은 건너뛴다.
        const base = baseRestoreTarget();
        if (base?.isConnected) focusForLifecycle(base);
        else if (restoreFocusTo?.isConnected) focusForLifecycle(restoreFocusTo);
      }
    };
  }, [rendered]);

  // container 준비되면 paint 전에 dialog 컨테이너로 focus — 첫 control에 주면 사용자 제스처 없이 자동으로
  // 열리는 모달에서 브라우저 :focus-visible 휴리스틱이 링을 그린다(컨테이너는 outline:none + SR이 dialog를
  // announce, Tab이 첫 control로 진입). children이 이미 focus를 잡았으면(consumer autoFocus) 덮지 않는다.
  // 위에 다른 모달 있으면(topmost 아님) 가로채지 않음. Dialog가 마운트된 이 시점에 popup mount node(=Dialog)도
  // 등록 — AnchoredPopup이 모달 안 열릴 때 참조.
  useLayoutEffect(() => {
    if (!container) return;
    setModalMountNode(container, dialogRef.current);
    // children(consumer autoFocus 포함)이 마운트를 마친 시점 — mount window를 닫는다.
    closeLifecycleFocusWindow(container);
    if (!isTopmostModal(container)) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (dialog.contains(document.activeElement)) return;
    focusForLifecycle(dialog);
  }, [container]);

  // open 중 level 변경 시 스택 우선순위 갱신 — 등록 시점 z로 굳으면 시각상 앞인데 inert/포커스 못 받는
  // 역전 발생. 승격(topmost로)이면 포커스를 가져오고, 강등(다른 모달 밑으로)이면 새 topmost로 넘긴다
  // (inert된 창에 포커스 잔류 방지). hadFocus는 inert 적용 전 캡처 — ancestor inert면 focus가 body로 날아감.
  useEffect(() => {
    if (!container) return;
    const hadFocus = container.contains(document.activeElement);
    setModalLevel(container, LEVEL_Z[level]);
    const focusInto = (root: HTMLElement) => {
      focusForLifecycle(root.querySelector<HTMLElement>('[role="dialog"]'));
    };
    const top = topmostModalEl();
    if (top === container) {
      if (!container.contains(document.activeElement)) focusInto(container);
    } else if (top && hadFocus) {
      focusInto(top);
    }
  }, [container, level]);

  // popup의 React 자손으로 열린 모달은 그 popup의 내부 입력이다. portal root를 소유 popup에 등록해
  // 모달 안 pointerdown이 소유 popup의 outside dismiss가 되지 않게 한다. 닫히는 모션 동안에도 backdrop이
  // 화면을 덮으므로 container가 있는 내내 등록한다. 모달은 modality 경계라 `closeOnEscape`와 무관하게
  // Escape를 소유 popup으로 넘기지 않는다 — 넘기면 소유 popup이 닫히며 그 자손인 이 모달도 언마운트된다.
  const registerWithOwner = useContext(PopupOwnerContext)?.register;
  useLayoutEffect(() => {
    if (!container) return;
    return registerWithOwner?.({ ref: { current: container }, consumesEscape: true });
  }, [container, registerWithOwner]);

  if (!container) return null;

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    // 중첩 모달에선 React 이벤트가 fiber 트리로 버블해 하위 모달 핸들러까지 내려온다. 이벤트가 이 모달
    // Backdrop DOM 밖(다른 모달/그 popup)에서 났으면 무시 — 안 그러면 상위 모달 안 popup의 Escape(양보
    // 경로는 stopPropagation 안 함)가 버블해 하위 모달을 닫는다. contains()는 결정적 DOM이라 synthetic
    // 라우팅과 무관, 자기 popup(Dialog로 portal)은 Backdrop 안이라 정상 처리.
    if (!event.currentTarget.contains(event.target as Node)) return;
    if (closeOnEscape && event.key === "Escape") {
      // 모달 위에 떠 자기 Escape를 실제로 처리하는 dismissable 레이어(예: 모달 안 AnchoredPopup이 Dialog로
      // portal)가 있으면 그게 topmost다. Escape는 topmost가 처리하므로 Escape를 소비하는 레이어가 있을 때만
      // 양보한다. `closeOnEscape=false`인 opt-out 레이어는 "escape" 토큰을 안 달아 여기서 안 걸리고 모달이
      // 닫힌다(dead gesture 방지). Modal은 generic 마커만 본다(특정 컴포넌트 결합 회피). 포커스 위치 무관.
      const backdrop = event.currentTarget;
      if (backdrop.querySelector(escapeLayerSelector("escape"))) return;
      event.stopPropagation();
      onClose();
      return;
    }
    // Tab cycle (WAI-ARIA APG) — `inert`는 바깥만 막아 마지막에서 Tab 시 브라우저 chrome으로 샐 수 있어
    // 양 끝에서 wrap(`inert`와 보완적). 트랩 범위 = Backdrop 전체(currentTarget): Dialog + 그 안으로 portal된
    // escape-layer 오버레이(모달 안 AnchoredPopup)까지. Dialog만 트랩하면 popup focusable이 Dialog 형제라
    // 트리거→popup Tab 진입 불가 + popup 마지막에서 모달 밖 탈출.
    if (event.key !== "Tab") return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const tabbables = getTabbables(event.currentTarget);
    if (tabbables.length === 0) {
      // tabbable 없으면 dialog 자체에 가둠.
      event.preventDefault();
      return;
    }
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    const focused = document.activeElement;
    if (event.shiftKey) {
      // Shift+Tab: 첫 tabbable이거나 dialog 자체에서 last로 wrap.
      if (focused === first || focused === dialog) {
        event.preventDefault();
        last.focus();
      }
    } else if (focused === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // pointerdown(가장 이른 hook)에 escape-layer 존재 기록 — usePopoverDismiss가 같은 pointerdown으로 popup을
  // 닫아도 unmount는 이 dispatch 이후라 여기선 아직 보인다.
  const handleBackdropPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // 바깥클릭을 실제로 소비하는 레이어("pointer" 토큰)만 실드. closeOnOutsidePointerDown=false인 opt-out
    // popup은 토큰이 없어 실드 안 됨 → backdrop 클릭이 모달로 fall-through(dead gesture 방지).
    escapeLayerAtPointerDown.current =
      event.currentTarget.querySelector(escapeLayerSelector("pointer")) != null;
  };

  const handlePointerDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    pointerDownOnBackdrop.current = event.target === event.currentTarget;
  };

  const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    // popup(escape-layer)이 떠 있던 제스처면 모달 보존 — 그 클릭은 topmost(popup)만 dismiss. Layer가
    // pointer-events:none이라 backdrop 클릭이 모달 가드까지 닿으므로 Escape와 같은 실드 필요.
    // 읽고 즉시 리셋 — pointerdown 없이 온 click(키보드 활성화 등)이 stale-true를 안 읽게.
    const shielded = escapeLayerAtPointerDown.current;
    escapeLayerAtPointerDown.current = false;
    if (shielded) return;
    if (
      closeOnBackdropClick &&
      pointerDownOnBackdrop.current &&
      event.target === event.currentTarget
    ) {
      onClose();
    }
  };

  return createPortal(
    <Backdrop
      data-c6-modal-backdrop=""
      // exit 시작(closing) 신호 — in-modal AnchoredPopup이 fade-out(MOTION_MS) 동안 즉시 self-close하도록.
      {...(phase === "closing" ? { [MODAL_EXITING_ATTR]: "" } : null)}
      $active={active}
      $blur={backdropBlur}
      $z={LEVEL_Z[level]}
      onPointerDown={handleBackdropPointerDown}
      onMouseDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Dialog
        ref={dialogRef}
        $maxWidth={MAX_WIDTH[size]}
        $maxHeight={maxHeight}
        $active={active}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={resolvedLabelledBy}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        className={className}
        style={style}
      >
        <ModalCloseContext.Provider value={onClose}>
          <ModalLabelContext.Provider value={labelRegistry}>{children}</ModalLabelContext.Provider>
        </ModalCloseContext.Provider>
      </Dialog>
    </Backdrop>,
    container,
  );
}

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBodyRoot,
  Footer: ModalFooterRoot,
  displayName: "Modal",
});
